// React hooks and part code management service
import { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import partCodeService from "services/part-code-service";
import changeLogService from "services/change-log-service";
import { AuthContext } from "context";
import formatPartCode from "helper/formatPartCode";
import { AbilityContext } from "Can";
import { useAbility } from "@casl/react";

// MUI and Material Dashboard Components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import AddIcon from "@mui/icons-material/Add";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import ArrowUpwardTwoToneIcon from '@mui/icons-material/ArrowUpwardTwoTone';
import ArrowDownwardTwoToneIcon from "@mui/icons-material/ArrowDownwardTwoTone";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import Snackbar from '@mui/material/Snackbar';

// Prime React components
import { TreeTable } from 'primereact/treetable';
import { Column } from 'primereact/column';
import "primereact/resources/themes/lara-light-cyan/theme.css";

// AddPartCode component (dialog)
import AddPartCode from "./add-part-code";
import EditPartCode from "./edit-entry";
import AddToAssembly from "./add-to-assembly";
import { Alert } from "@mui/material";

function BOMTab() {
    const [OpenAddPartCodeDialog, setOpenAddPartCodeDialog] = useState(false);
    const [OpenAddToAssemblyDialog, setOpenAddToAssemblyDialog] = useState(false);
    const [openEditPartCodeDialog, setOpenEditPartCodeDialog] = useState(false);
    const [editPartCodeData, setEditPartCodeData] = useState({});
    const [partCode, setPartCode] = useState({});
    const { id } = useParams();
    const [selectedPartCodeId, setSelectedPartCodeId] = useState("");
    const APP_URL = process.env.REACT_APP_URL;


    const [children, setChildren] = useState([]); // Stores the children of the selected part code

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");

    const authContext = useContext(AuthContext);
    const ability = useAbility(AbilityContext);

    useEffect(() => { // Fetches the children of the selected part code
        (async () => {
            const response = await partCodeService.getChildren(id);
            setPartCode(response.data.attributes);
        })();
    }, []);

    // Handles the opening of the AddPartCode dialog
    const HandleOpenAddPartCodeDialog = async (id) => { // Opens the AddPartCode dialog
        setSelectedPartCodeId(id);
        // Fetches the children of the part code to which we want to add the new part code
        const response = await partCodeService.getPartById(id);
        setChildren(response.data.attributes.children);
        setOpenAddPartCodeDialog(true);
    };

    // Closes the AddPartCode dialog and refreshes the tree table
    const CloseAddPartCodeDialog = async () => {
        setOpenAddPartCodeDialog(false);
        setSelectedPartCodeId("");
        const response = await partCodeService.getChildren(id);
        setPartCode(response.data.attributes);
    };

    const handleOpenEditPartCodeDialog = async (node) => {
        setEditPartCodeData(node);
        setOpenEditPartCodeDialog(true);
    }

    const handleCloseEditPartCodeDialog = async (flag, qty, notes, node) => {
        if (!node || !node.key || flag === "cancel") {
            setOpenEditPartCodeDialog(false);
            return;
        }
        const deleteId = node.key;
        const parentId = findParent(treeNodes, node.data.path);

        // Fetches the parent partcode of the part code to be edited
        const parentPartCode = await partCodeService.getPartById(parentId);

        // Removes the selected partcode from its parent's children array
        const childrenArr = parentPartCode.data.attributes.children || [];
        const index = childrenArr.findIndex(child => child?.part === deleteId);

        const updatedChildren = [...childrenArr];
        const updatedChild = { ...(updatedChildren[index] || {}), part: deleteId, Quantity: qty, text: notes };

        if (index >= 0) {
            updatedChildren.splice(index, 1, updatedChild);
        } else {
            updatedChildren.push(updatedChild);
        }

        const payload = {
            data: {
                type: "PartCode",
                id: parentId,
                attributes: {
                    children: updatedChildren,
                }
            }
        }

        await partCodeService.updateChildren(payload, parentId);

        // Refreshes the tree table
        const response = await partCodeService.getChildren(id);
        setPartCode(response.data.attributes);

        setEditPartCodeData({});
        setOpenEditPartCodeDialog(false);
    }

    const HandleOpenAddToAssemblyDialog = async () => {
        setOpenAddToAssemblyDialog(true);
    };

    const CloseAddToAssemblyDialog = async () => {
        setOpenAddToAssemblyDialog(false);
    };

    // Handles the deletion of a part code
    const handleDelete = async (node) => {

        const deleteId = node.key;
        const parentId = findParent(treeNodes, node.data.path);

        // Fetches the parent partcode of the part code to be deleted
        const parentPartCode = await partCodeService.getPartById(parentId);

        // Removes the selected partcode from its parent's children array
        const payload = {
            data: {
                type: "PartCode",
                id: parentId,
                attributes: {
                    children: parentPartCode.data.attributes.children.filter(child => child.part !== deleteId),

                }
            }
        };
        await partCodeService.updateChildren(payload, parentId);

        // Refreshes the tree table
        const response = await partCodeService.getChildren(id);
        setPartCode(response.data.attributes);

        const response2 = await partCodeService.getPartById(id);
        const assembly = response2.data.attributes;
        const changeLog = {
            data: {
                type: "changeLog",
                attributes: {
                    partCode: deleteId,
                    change: {
                        type: "Removed from BOM",
                        description: `Deleted part code from E4A-${assembly.partCategory.categoryCode}-${fillWithZeros(assembly.unique_num)}-${assembly.revision_num}`,
                        date: new Date(),
                        user: await authContext.getCurrentUser()
                    }
                }
            }
        }
        // Adds the change to the change Log
        await changeLogService.addChange(changeLog);
    }

    const handleOpenSnackbar = (message) => {
        setSnackbarMessage(message);
        setOpenSnackbar(true);
    }

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
        setSnackbarMessage("");
    }

    // Formats the fetched partcode's children to match the tree table's structure
    const formatPartCodeData = (part, path = '0') => {
        if (!part || typeof part !== 'object' || part._id === undefined) return null; // Return null if part is not an object (Meaning it has not been populated)
        return {
            key: part._id,
            data: {
                // The part code column is clickable and redirects to the part code's specific view page
                name: <a
                    href={`${APP_URL}/part-code-management/view-part-code/${part._id}`}
                    style={{ textDecoration: 'none', color: 'blue' }}
                >
                    {formatPartCode(part)}
                </a>,
                category: part.description,
                path: path,
                text: part.text ? part.text : "Root Part Code",
                Quantity: part.Quantity ? part.Quantity : 1
            },
            // Recursive call to populate the struct with the part's children too
            children: part?.children
                ? part.children
                    .filter(child => typeof child === 'object')
                    .map((child, index) => formatPartCodeData({ ...child.part, text: child.text, Quantity: child.Quantity }, `${path}-${index}`))
                    .filter(Boolean)
                : [],
            parent: part.parent ? part.parent : null
        };
    };

    const fillWithZeros = (num) => {
        return num.toString().padStart(5, '0');
    };
    // Format the part code data to match the nodes structure
    const [treeNodes, setTreeNodes] = useState([]);

    // Update treeNodes whenever partCode data is fetched from the server
    useEffect(() => {
        if (partCode && partCode._id) {
            setTreeNodes([formatPartCodeData(partCode)]);
        }
    }, [partCode]);

    // Find the parent of the selected part code in the tree table
    const findParent = (nodes, targetPath) => {

        // Handle array of nodes at root level
        if (Array.isArray(nodes)) {
            for (let node of nodes) {
                const result = findParent(node, targetPath);
                if (result) return result;
            }
            return null;
        }

        // Check children of current node
        if (nodes.children) {
            for (let child of nodes.children) {
                if (child.data.path === targetPath) {
                    return nodes.key; // Return parent's key when child is found
                }
                const result = findParent(child, targetPath);
                if (result) return result;
            }
        }
        return null;
    };

    const containerSpacing = {
        marginTop: { xs: 4, md: 10 },
        marginBottom: { xs: 4, md: 8 }
    };

    const swapSibling = async (node, direction) => {
        if (!node || (direction !== "up" && direction !== "down")) return;
        if (node.key === id) return; // root cannot move

        const parentId = findParent(treeNodes, node.data.path);
        if (!parentId) return;

        try {
            // Fetch parent to get current children order
            const parentPartCode = await partCodeService.getPartById(parentId);
            const childrenArr = [...(parentPartCode.data.attributes.children || [])];

            const index = childrenArr.findIndex(child => child?.part === node.key);
            if (index === -1) return;

            // If first or last, show snackbar and exit
            if (direction === "up" && index === 0) {
                handleOpenSnackbar("This item is already at the top.");
                return;
            }
            if (direction === "down" && index === childrenArr.length - 1) {
                handleOpenSnackbar("This item is already at the bottom.");
                return;
            }

            const swapWithIndex = direction === "up" ? index - 1 : index + 1;

            // Swap
            [childrenArr[index], childrenArr[swapWithIndex]] = [childrenArr[swapWithIndex], childrenArr[index]];

            const payload = {
                data: {
                    type: "PartCode",
                    id: parentId,
                    attributes: {
                        children: childrenArr
                    }
                }
            };

            await partCodeService.updateChildren(payload, parentId);

            // Refresh tree
            const response = await partCodeService.getChildren(id);
            setPartCode(response.data.attributes);

        } catch (e) {
            console.error("Swap failed", e);
        }
    };

    return (
        <MDBox {...containerSpacing}>

            <EditPartCode
                handleClose={handleCloseEditPartCodeDialog}
                open={openEditPartCodeDialog}
                node={editPartCodeData}
            />

            {/* AddPartCode dialog */}
            <AddPartCode
                handleClose={CloseAddPartCodeDialog}
                open={OpenAddPartCodeDialog}
                id={selectedPartCodeId}
                children={children}
                setChildren={setChildren}
            />

            {/* Add to assembly dialog */}

            <AddToAssembly
                handleClose={CloseAddToAssemblyDialog}
                open={OpenAddToAssemblyDialog}
                id={id}
            />

            <Snackbar
                open={openSnackbar}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity="warning"
                    sx={{ width: '100%', alignItems: 'center' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            <Grid container justifyContent="center">
                <Grid item xs={12} sm={12} md={12}>

                    <Card style={{ borderRadius: '8px' }}>
                        <MDBox p={3} lineHeight={1} display="flex" justifyContent="space-between"
                        >
                            <MDTypography variant="h3" fontWeight="medium" textAlign="left">
                                Bill of Materials
                            </MDTypography>

                            {partCode.partCategory &&
                                ((partCode.partCategory.parentCategory !== "ASSEMBLIES") &&
                                    ability.can("Associate", "PartCode") &&
                                    (
                                        <MDButton
                                            variant="gradient"
                                            color="info"
                                            size="small"
                                            onClick={HandleOpenAddToAssemblyDialog}
                                        >
                                            Add to an assembly
                                        </MDButton>
                                    ))}

                        </MDBox>
                        <MDBox>

                            {/* TreeTable */}
                            <TreeTable
                                value={treeNodes}
                                className="p-datatable-sm"
                                showGridlines
                                tableStyle={{ minWidth: '40rem' }}
                                pt={{
                                    root: { style: { borderRadius: '8px' } },
                                    header: { style: { padding: '1rem' } },
                                    wrapper: { style: { padding: '1.5rem' } },
                                    body: { style: { borderRadius: '8px' } }
                                }}
                                reorderableColumns
                            >
                                {/*Columns*/}
                                {ability.can("Associate", "PartCode") && (
                                    <Column
                                        header=""
                                        style={{ width: '4%' }}
                                        body={(node) => (
                                            <>
                                                {!(node.key === id) && (
                                                    <Tooltip title="Move Up">
                                                        <IconButton onClick={() => swapSibling(node, "up")}>
                                                            <ArrowUpwardTwoToneIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {!(node.key === id) && (
                                                    <Tooltip title="Move Down">
                                                        <IconButton onClick={() => swapSibling(node, "down")}>
                                                            <ArrowDownwardTwoToneIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </>
                                        )}
                                    />
                                )}

                                <Column
                                    field="name"
                                    header=" Part Code"
                                    expander
                                    style={{ width: '25%', paddingLeft: '1rem', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}
                                />
                                <Column
                                    field="text"
                                    header="Notes"
                                    style={{ width: '20%', paddingLeft: '1rem' }}
                                />
                                <Column
                                    field="Quantity"
                                    header="Qty"
                                    style={{ width: '5%', paddingLeft: '1rem' }}
                                />
                                <Column
                                    field="category"
                                    header="Description"
                                    style={{ width: '26%', paddingLeft: '1rem' }}
                                />

                                {/* Actions column wth delete and add buttons*/}
                                {ability.can("Associate", "PartCode") && (
                                    <Column
                                        header="Actions"
                                        style={{ width: '20%', paddingLeft: '1rem' }}
                                        body={(node) => (
                                            <>
                                                {!(node.key === id) && (
                                                    <Tooltip
                                                        title="Delete Part"
                                                        onClick={() => handleDelete(node)}
                                                    >
                                                        <IconButton>
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {!(node.key === id) && (
                                                    <Tooltip
                                                        title="Edit Part"
                                                        onClick={() => handleOpenEditPartCodeDialog(node)}
                                                    >
                                                        <IconButton>
                                                            <EditTwoToneIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {node.children && (
                                                    <Tooltip title="Add Part Code" onClick={() => HandleOpenAddPartCodeDialog(node.key)}>
                                                        <IconButton color="success">
                                                            <AddIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                            </>
                                        )}
                                    />)}
                            </TreeTable>
                        </MDBox>
                    </Card>
                </Grid>
            </Grid>
        </MDBox>
    );
}
export default BOMTab;